import React, { useCallback, useMemo } from 'react';
import get from 'lodash/get';

/* Client Store */
import { useNavigationStorePrePendMenuByPath } from 'src/store/client/store.navigation';

/*  Utils */
import { prevented } from '@utils/event';
import { getUpsellData } from 'src/constants/upsell';

/* Components */
import MenuTemplate from '../MenuTemplate';
import { ICategory, ISku, ISubCategory, ISubCategoryType } from 'src/constants/nav';
import { MenuItemProps, TMenuItemVariant } from '../MenuTemplate/MenuTemplate';
import NavSubCategoryUpsell from 'src/components/PageLayouts/Header/Standard/TopNavItems/Parts/NavSubCategoryUpsell';

/* Types */
import { LayeredMenuProps } from './layeredMenu.types';

const LayeredMenu = ({ visible, layer, onSelectChild, onBackClick, skuLinkPrefix }: LayeredMenuProps) => {
  const prePendMenuByPath = useNavigationStorePrePendMenuByPath();

  const skuLink = useCallback(
    (sku: ISku) => (skuLinkPrefix ? `${prePendMenuByPath}/${sku.seo_name}/${skuLinkPrefix}` : `${prePendMenuByPath}/${sku.seo_name}`),
    [prePendMenuByPath, skuLinkPrefix],
  );

  const headerItems = [
    {
      text: layer.name,
      onBackClick: prevented(() => onBackClick?.()),
    },
  ];

  const children = useMemo(() => {
    const upsellData = getUpsellData({ category: layer, device: 'mobile' });
    if (!upsellData.text) return null;
    return <NavSubCategoryUpsell.MobileTablet {...upsellData} containerClassName="marginTop-huge" />;
  }, [layer]);

  const secondaryItems = useMemo<Partial<MenuItemProps>[]>(() => {
    const categories: ICategory[] | undefined = get(layer, 'categories');
    const subCategories: ISubCategory[] | undefined = get(layer, 'subCategories');
    const types: ISubCategoryType[] | undefined = get(layer, 'types');
    const skus: ISku[] | undefined = get(layer, 'skus');

    if (categories !== undefined)
      return (categories as ICategory[]).map(category => ({
        text: category.name,
        onClick: prevented(() => onSelectChild!(category)),
      }));
    if (types !== undefined)
      return (types as ISubCategoryType[])
        .map(subCategoryType => [
          {
            text: subCategoryType.name,
            variant: 'subCategoryType' as TMenuItemVariant,
          },
          ...subCategoryType.skus.map(sku => ({
            text: sku.shortName || sku.name,
            link: skuLink(sku),
          })),
        ])
        .flat();

    if (subCategories !== undefined)
      return (subCategories as ISubCategory[]).map(subCategory => {
        if (!subCategory.types)
          return {
            text: subCategory.name,
            link: skuLink(subCategory.sku!),
          };

        return {
          text: subCategory.name,
          onClick: prevented(() => onSelectChild!(subCategory)),
        };
      });
    if (skus !== undefined)
      return (skus as ISku[]).map(sku => ({
        text: sku.shortName || sku.name,
        link: skuLink(sku),
      }));
    return [];
  }, [layer, onSelectChild, skuLink]);
  return (
    <MenuTemplate
      slide
      visible={visible}
      headerItems={headerItems}
      secondaryItems={secondaryItems}
      dataTestId="nav-mobile-category-menu"
      children={children}
    />
  );
};

export default React.memo(LayeredMenu);
